import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import { meetingsAPI } from '../services/api';
import { toast } from 'react-hot-toast';
import CustomPagination from '../components/CustomPagination';

const Meetings = () => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      const response = await meetingsAPI.getMeetings({
        page: page + 1,
        limit: rowsPerPage
      });
      setMeetings(response.data.data);
      setTotalCount(response.data.total || response.data.data.length);
    } catch (error) {
      console.error('Meetings API Error:', error);
      const errorMessage = error.response?.data?.message || 
                         error.message || 
                         'Failed to fetch meetings';
      toast.error(errorMessage, {
        id: 'meetings-fetch-error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeetings();
  }, [page, rowsPerPage]);

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = async (meeting) => {
    try {
      setLoading(true);
      const response = await meetingsAPI.getMeetingDetails(meeting.meetingId);
      setSelectedMeeting(response.data.data);
      setOpenDialog(true);
    } catch (error) {
      console.error('Meeting API Error:', error);
      const errorMessage = error.response?.data?.message || 
                         error.message || 
                         'Failed to fetch meeting details';
      toast.error(errorMessage, {
        id: 'meeting-details-error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedMeeting(null);
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime || startTime == 'null' || endTime === 'null') return 'N/A';
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diff = end - start;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Meetings</Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sr. No.</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Start Time</TableCell>
              <TableCell>End Time</TableCell>
              <TableCell>Room Name</TableCell>
              <TableCell>Participants</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : meetings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No meetings found
                </TableCell>
              </TableRow>
            ) : (
              meetings.map((meeting, index) => (
                <TableRow key={meeting.id}>
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>{meeting.title}</TableCell>
                  <TableCell>{new Date(meeting.startedAt).toLocaleString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}</TableCell>
                  <TableCell>{meeting.endedAt !== null ? new Date(meeting.endedAt).toLocaleString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  }): '-'}</TableCell>
                  <TableCell>{meeting.name || 'N/A'}</TableCell>
                  <TableCell>{meeting.participantCount || 0}</TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog(meeting)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomPagination
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={(e) => handleChangeRowsPerPage({ target: { value: e.target.value } })}
      />

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Meeting Details</DialogTitle>
        <DialogContent>
          {selectedMeeting && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedMeeting.title}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Room: {selectedMeeting?.roomname || 'N/A'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Duration: {selectedMeeting.endedAt !== null ? formatDuration(selectedMeeting.startedAt, selectedMeeting.endedAt) : 'Ongoing'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Total Attendees: {selectedMeeting.participants?.length || 0}
              </Typography>
              
              <Typography variant="h6" sx={{ mt: 2 }} gutterBottom>
                Attendees
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Join Time</TableCell>
                      <TableCell>Duration</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedMeeting.participants?.map((attendee) => (
                      <TableRow key={attendee.id}>
                        <TableCell>{attendee.username}</TableCell>
                        <TableCell>{attendee.entryTimeAt !== null ? new Date(attendee.entryTimeAt).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        }) : new Date(selectedMeeting.startedAt).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        })}</TableCell>
                        <TableCell>
                          {formatDuration(attendee.entryTimeAt, attendee.exitTimeAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Meetings; 